/**
 * get-started — derive result payload from answers (extend later)
 *
 * Metric scores: assign `window.rankingMetricsScore` before this script loads (theme asset or inline).
 * Supplement × metric scores: `window.metrics_supplement_research` (see metrics_supplement_research.js).
 */
window.GetStartedCompute = (() => {
  const MIN_DURATION_MS = 3000;

  /** Ranking metric key → key on window.metrics_supplement_research when names differ */
  const SUPPLEMENT_TABLE_METRIC_ALIASES = {
    vo2max: 'vo2_max',
    sleep_disruption: 'sleep_disruptions',
  };

  const GOAL_RANKING_LABEL = {
    apple_watch: {
      heart: 'Heart',
      training_load: 'Training Load',
      vitals: 'Vitals',
      cardio_fitness: 'Cardio Fitness',
      sleep: 'Sleep',
    },
    garmin: {
      fitness_age: 'Fitness Age',
      training_readiness: 'Training Readiness',
      vo2_max: 'VO2 Max',
      body_battery: 'Body Battery',
      sleep_garmin: 'Sleep',
    },
    whoop: {
      stress: 'Stress',
      strain: 'Strain',
      healthspan: 'Healthspan',
      sleep: 'Sleep',
      recovery: 'Recovery',
    },
  };

  function getRankingMetricsScore() {
    if (
      typeof window !== 'undefined' &&
      window.rankingMetricsScore &&
      typeof window.rankingMetricsScore === 'object'
    ) {
      return window.rankingMetricsScore;
    }
    return {};
  }

  /**
   * @param {Record<string, [number, [string, string][]]>} map
   * @returns {Record<string, [number, [string, string][]]>} same shape, keys ordered highest score first
   */
  function sortMetricsByScoreDescending(map) {
    const entries = Object.entries(map).sort((a, b) => {
      const scoreA = a[1][0];
      const scoreB = b[1][0];
      if (scoreB !== scoreA) return scoreB - scoreA;
      return a[0].localeCompare(b[0]);
    });
    return Object.fromEntries(entries);
  }

  function coupleKey(c) {
    return `${c[0]}\0${c[1]}`;
  }

  /**
   * Before sorting: if `sleep_quality` is present and at least one of `deep_sleep` / `rem_sleep`
   * is present, fold sleep_quality into those rows (score + merged contributors) and remove it.
   * If neither deep_sleep nor rem_sleep exists, leave sleep_quality unchanged.
   * @param {Record<string, [number, [string, string][]]>} map — mutated in place
   */
  function applySleepQualityRedistribution(map) {
    const sqRow = map.sleep_quality;
    if (!sqRow) return;

    const hasDeep = Object.prototype.hasOwnProperty.call(map, 'deep_sleep');
    const hasRem = Object.prototype.hasOwnProperty.call(map, 'rem_sleep');
    if (!hasDeep && !hasRem) return;

    const [sqScore, sqContributors] = sqRow;

    function mergeInto(metricKey) {
      const row = map[metricKey];
      if (!row) return;
      row[0] += sqScore;
      const seen = new Set(row[1].map(coupleKey));
      for (const c of sqContributors) {
        const k = coupleKey(c);
        if (!seen.has(k)) {
          seen.add(k);
          row[1].push(c);
        }
      }
    }

    if (hasDeep) mergeInto('deep_sleep');
    if (hasRem) mergeInto('rem_sleep');

    delete map.sleep_quality;
  }

  /** Wearable sleep goals — always labeled "Sleep" (no "Sleep Garmin" / device suffix). */
  function isSleepGoalId(goalId) {
    if (!goalId || typeof goalId !== 'string') return false;
    return goalId === 'sleep' || goalId === 'sleep_garmin' || goalId.startsWith('sleep_');
  }

  /**
   * @param {string[]} goalKeys — "deviceId::goalId" composite keys
   * @returns {Record<string, [number, [string, string][]]>} metric → [sumScore, ...[deviceId, goalLabel]], ranked
   */
  function buildUserMetricsRanking(goalKeys) {
    const table = getRankingMetricsScore();
    /** @type {Record<string, [number, [string, string][]]>} */
    const acc = {};

    for (const key of goalKeys) {
      const { deviceId, goalId } = parseGoalKey(key);
      if (!deviceId || !goalId) continue;
      const goalLabel = GOAL_RANKING_LABEL[deviceId]?.[goalId];
      if (!goalLabel) continue;
      const block = table[deviceId]?.[goalLabel];
      if (!block || typeof block !== 'object') continue;

      for (const [metricKey, rawScore] of Object.entries(block)) {
        if (typeof rawScore !== 'number' || !Number.isFinite(rawScore)) continue;
        if (!acc[metricKey]) {
          acc[metricKey] = [0, []];
        }
        const row = acc[metricKey];
        row[0] += rawScore;
        const couple = /** @type {[string, string]} */ ([deviceId, goalLabel]);
        const seen = row[1].some((c) => c[0] === couple[0] && c[1] === couple[1]);
        if (!seen) {
          row[1].push(couple);
        }
      }
    }

    applySleepQualityRedistribution(acc);
    return sortMetricsByScoreDescending(acc);
  }

  /**
   * Ordered list with explicit 1-based rank (same order as userMetricsRanking).
   * @param {Record<string, [number, [string, string][]]>} rankedMap
   * @returns {{ metric: string, score: number, rank: number, contributors: [string, string][] }[]}
   */
  /** Human-readable goal labels for results UI (same order as `goals` keys). */
  function selectedGoalDisplayLabels(goalKeys) {
    return goalKeys.map((key) => {
      const { deviceId, goalId } = parseGoalKey(key);
      if (isSleepGoalId(goalId)) return 'Sleep';
      const fromMap = deviceId && goalId ? GOAL_RANKING_LABEL[deviceId]?.[goalId] : null;
      const label = fromMap || simpleGoalLabel(goalId);
      return label.replace(/\bVO2 Max\b/gi, 'VO\u2082 Max');
    });
  }

  function buildUserMetricsRankingOrdered(rankedMap) {
    let rank = 0;
    return Object.entries(rankedMap).map(([metric, row]) => {
      const [score, contributors] = row;
      return {
        metric,
        score,
        rank: ++rank,
        contributors,
      };
    });
  }

  function getMetricsSupplementResearch() {
    if (typeof window === 'undefined') return {};
    const v = window.metrics_supplement_research;
    if (v && typeof v === 'object') return v;
    return {};
  }

  /**
   * Extract numeric supplement score from `metrics_supplement_research[metric][supplement]`.
   * Expected: `{ supplement_score: number }` (but tolerate other shapes).
   * @param {any} cell
   * @returns {number|null}
   */
  function extractSupplementScore(cell) {
    if (!cell || typeof cell !== 'object') return null;
    const v = cell.supplement_score;
    if (typeof v !== 'number' || !Number.isFinite(v)) return null;
    return v;
  }

  function resolveSupplementMetricKey(metric, table) {
    if (table[metric]) return metric;
    const alt = SUPPLEMENT_TABLE_METRIC_ALIASES[metric];
    if (alt && table[alt]) return alt;
    return metric;
  }

  /**
   * Supplement names for one metric, best score first (then name).
   * Only entries with score > 0 are candidates — metrics with no positive match are skipped by the allocator.
   * @param {string} metric
   * @param {Record<string, Record<string, any>>} table
   */
  function supplementsSortedForMetric(metric, table) {
    const key = resolveSupplementMetricKey(metric, table);
    const row = table[key];
    if (!row || typeof row !== 'object') return [];
    return Object.entries(row)
      .map(([name, cell]) => [name, extractSupplementScore(cell)])
      .filter(([, score]) => typeof score === 'number' && score > 0)
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1];
        return a[0].localeCompare(b[0]);
      })
      .map(([name]) => name);
  }

  /**
   * Walk ranked metrics in order; each pass tries at most one new supplement per metric (best not already chosen).
   * Supplements with score 0 on that metric are never picked; a metric with no unused positive-scoring supplement is skipped.
   * Repeats passes until stack is full or no progress.
   * @param {{ metric: string, rank: number }[]} orderedMetrics
   * @param {number|null|undefined} supplementCount
   * @returns {{ supplements: string[], details: { supplement: string, metric: string, metricRank: number }[] }}
   */
  function selectRecommendedSupplements(orderedMetrics, supplementCount) {
    const table = getMetricsSupplementResearch();
    const cap =
      typeof supplementCount === 'number' && supplementCount > 0
        ? Math.floor(supplementCount)
        : 0;
    if (!cap || !orderedMetrics.length) {
      return { supplements: [], details: [] };
    }

    const supplements = [];
    const details = [];
    const used = new Set();

    while (supplements.length < cap) {
      let addedThisRound = false;
      for (const { metric, rank } of orderedMetrics) {
        if (supplements.length >= cap) break;
        const sortedNames = supplementsSortedForMetric(metric, table);
        const pick = sortedNames.find((name) => !used.has(name));
        if (pick) {
          supplements.push(pick);
          used.add(pick);
          details.push({ supplement: pick, metric, metricRank: rank });
          addedThisRound = true;
        }
      }
      if (!addedThisRound) break;
    }

    return { supplements, details };
  }

  /**
   * @param {Record<string, Record<string, any>>} table
   * @param {string} metric
   * @param {string} supplement
   * @returns {number|null}
   */
  function supplementScoreOnMetric(table, metric, supplement) {
    const key = resolveSupplementMetricKey(metric, table);
    const row = table[key];
    if (!row || typeof row !== 'object') return null;
    return extractSupplementScore(row[supplement]);
  }

  /**
   * @param {{ metric: string, score: number, rank: number, contributors: [string, string][] }[]} orderedRows
   * @param {string[]} recommendedSupplements
   * @param {Record<string, Record<string, any>>} table
   */
  function enrichUserMetricsRankingOrdered(orderedRows, recommendedSupplements, table) {
    return orderedRows.map((row) => {
      const pairs = [];
      for (const name of recommendedSupplements) {
        const s = supplementScoreOnMetric(table, row.metric, name);
        if (s != null && s > 0) {
          pairs.push([name, s]);
        }
      }
      pairs.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      return {
        ...row,
        supplements: Object.fromEntries(pairs),
      };
    });
  }

  /**
   * @param {{ supplement: string, metric: string, metricRank: number }[]} details
   * @param {{ metric: string }[]} orderedMetricRows
   * @param {Record<string, Record<string, any>>} table
   */
  function enrichRecommendedSupplementsDetail(details, orderedMetricRows, table) {
    const metricsInOrder = orderedMetricRows.map((r) => r.metric);
    return details.map((d) => {
      const metricsRankedForSupplement = [];
      let aggregateSupplementScore = 0;
      for (const metric of metricsInOrder) {
        const s = supplementScoreOnMetric(table, metric, d.supplement);
        if (s != null) {
          aggregateSupplementScore += s;
          if (s > 0) {
            metricsRankedForSupplement.push({ metric, score: s });
          }
        }
      }
      metricsRankedForSupplement.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.metric.localeCompare(b.metric);
      });
      return {
        ...d,
        metricsRankedForSupplement,
        aggregateSupplementScore,
      };
    });
  }

  /**
   * @param {object} answers — { devices: string[], goals: string[], supplementCount }
   *   goals entries are "deviceId::goalId" composite keys
   */
  async function compute(answers) {
    const started = Date.now();

    const devices = normalizeDevices(answers);
    const goals = Array.isArray(answers.goals) ? answers.goals : [];
    const supplementCount = answers.supplementCount ?? null;
    const userMetricsRanking = buildUserMetricsRanking(goals);
    const userMetricsRankingOrderedBase = buildUserMetricsRankingOrdered(userMetricsRanking);
    const { supplements: recommendedSupplements, details: recommendedSupplementsDetailBase } =
      selectRecommendedSupplements(userMetricsRankingOrderedBase, supplementCount);

    const supplementTable = getMetricsSupplementResearch();
    const userMetricsRankingOrdered = enrichUserMetricsRankingOrdered(
      userMetricsRankingOrderedBase,
      recommendedSupplements,
      supplementTable
    );
    const recommendedSupplementsDetail = enrichRecommendedSupplementsDetail(
      recommendedSupplementsDetailBase,
      userMetricsRankingOrderedBase,
      supplementTable
    );

    const result = {
      devices,
      goals,
      selectedGoalLabels: selectedGoalDisplayLabels(goals),
      supplementCount,
      userMetricsRanking,
      userMetricsRankingOrdered,
      recommendedSupplements,
      recommendedSupplementsDetail,
      headline: buildHeadline(devices, goals, supplementCount),
      bullets: buildBullets(
        devices,
        goals,
        supplementCount,
        userMetricsRanking,
        recommendedSupplements
      ),
      generatedAt: new Date().toISOString(),
    };

    const elapsed = Date.now() - started;
    const wait = Math.max(0, MIN_DURATION_MS - elapsed);
    if (wait > 0) {
      await new Promise((r) => setTimeout(r, wait));
    }

    return result;
  }

  function normalizeDevices(answers) {
    if (Array.isArray(answers.devices) && answers.devices.length) {
      return answers.devices;
    }
    if (answers.device) {
      return [answers.device];
    }
    return [];
  }

  function buildHeadline(devices, goalKeys, count) {
    const c = count != null ? String(count) : '—';
    const g = goalKeys.length ? `${goalKeys.length} goals` : 'your goals';
    const d = formatDevicesPhrase(devices);
    return `Your ${c}-supplement plan for ${g} (${d})`;
  }

  function formatDevicesPhrase(devices) {
    if (!devices.length) return 'your devices';
    if (devices.length === 1) return labelDevice(devices[0]);
    const labels = devices.map(labelDevice);
    if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, & ${labels[labels.length - 1]}`;
  }

  function buildBullets(devices, goalKeys, count, userMetricsRanking, recommendedSupplements) {
    const out = [];
    if (devices.length) {
      out.push(
        devices.length === 1
          ? `Device: ${labelDevice(devices[0])}`
          : `Devices: ${devices.map(labelDevice).join(', ')}`
      );
    }
    if (goalKeys.length) {
      const labels = goalKeys.map(goalKeyToShortLabel);
      out.push(`Priority goals: ${labels.join(', ')}`);
    }
    if (count != null) out.push(`Stack size: ${count} supplements`);
    const nMetrics = userMetricsRanking ? Object.keys(userMetricsRanking).length : 0;
    if (nMetrics) out.push(`Ranked metrics: ${nMetrics}`);
    if (recommendedSupplements && recommendedSupplements.length) {
      out.push(`Suggested stack: ${recommendedSupplements.join(', ')}`);
    }
    out.push('Next: wire product rules and sections from this payload.');
    return out;
  }

  function goalKeyToShortLabel(key) {
    const { deviceId, goalId } = parseGoalKey(key);
    if (isSleepGoalId(goalId)) return 'Sleep';
    const goalLabel = simpleGoalLabel(goalId);
    if (!deviceId) return goalLabel;
    return `${goalLabel} (${labelDevice(deviceId)})`;
  }

  function parseGoalKey(key) {
    if (typeof key !== 'string') return { deviceId: null, goalId: '' };
    const sep = '::';
    const i = key.indexOf(sep);
    if (i < 0) return { deviceId: null, goalId: key };
    return { deviceId: key.slice(0, i), goalId: key.slice(i + sep.length) };
  }

  function simpleGoalLabel(goalId) {
    return String(goalId || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function labelDevice(id) {
    const map = {
      apple_watch: 'Apple Watch',
      garmin: 'Garmin',
      whoop: 'Whoop',
    };
    return map[id] || id;
  }

  return {
    compute,
    MIN_DURATION_MS,
    buildUserMetricsRanking,
    buildUserMetricsRankingOrdered,
    sortMetricsByScoreDescending,
    selectRecommendedSupplements,
      getMetricsSupplementResearch,
    supplementScoreOnMetric,
    enrichUserMetricsRankingOrdered,
    enrichRecommendedSupplementsDetail,
  };
})();
